import React, { useEffect, useRef, useState } from 'react';

// Hook to detect screen size for responsive viewBox
function useScreenSize() {
  const [isLargeScreen, setIsLargeScreen] = useState(false);
  
  useEffect(() => {
    const checkScreenSize = () => {
      setIsLargeScreen(window.innerWidth >= 992);
    };
    
    // Set initial value
    checkScreenSize();
    
    // Add event listener
    window.addEventListener('resize', checkScreenSize);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);
  
  return isLargeScreen;
}

const PotredStroke = ({
  text = "Potred",
  className = "",
  stroke = 2,          // stroke width in px
  duration = 1.2,      // seconds (match logo timing)
  size = "large",      // "large" for hero, "medium" for nav/footer, "small" for compact
  responsive = false,  // enable responsive sizing
}) => {
  const ref = useRef(null);
  const [L, setL] = useState(0);
  const isLargeScreen = useScreenSize();
  const [animationKey, setAnimationKey] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    // get total text length (SVG computes from glyphs of the current font)
    const len = ref.current.getComputedTextLength?.() ?? 0;
    setL(Math.ceil(len));
  }, [text]);

  // Periodic animation effect (match logo timing)
  useEffect(() => {
    if (responsive && size === "medium") { // Only for nav/footer responsive text
      const startPeriodicAnimation = () => {
        intervalRef.current = setInterval(() => {
          setAnimationKey(prev => prev + 1); // Trigger re-animation
        }, 6000); // Same as logo: 6 seconds
      };

      // Start periodic animation
      startPeriodicAnimation();

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [responsive, size]);

  // Define responsive sizing configurations
  const getResponsiveSizing = () => {
    if (!responsive) {
      return {
        viewBox: size === "small" ? "0 0 300 60" : size === "medium" ? "0 0 400 80" : "0 0 900 200",
        className: size === "small" ? "w-full h-full" : size === "medium" ? "w-full h-full" : "w-full max-w-[800px] h-auto",
        fontSize: size === "small" ? "32px" : size === "medium" ? "48px" : "120px"
      };
    }
    
    // Responsive sizing based on viewport and size prop
    if (size === "medium") { // Navigation/Footer responsive sizing
      return {
        // Use different viewBox widths for different screen sizes
        viewBox: isLargeScreen ? "0 0 250 80" : "0 0 180 80",
        className: "w-full h-full",
        // Scale font size responsively: larger max for desktop
        fontSize: "clamp(28px, 4vw, 50px)" // Mobile: ~28px, Tablet: ~31px, Desktop: up to 50px
      };
    }
    
    if (size === "small") {
      return {
        viewBox: "0 0 300 60",
        className: "w-full h-full",
        fontSize: "clamp(24px, 3.5vw, 32px)" // Scales smaller for compact usage
      };
    }
    
    // Large size (hero) gets more dramatic responsive scaling
    return {
      viewBox: "0 0 900 200",
      className: "w-full max-w-[800px] h-auto",
      fontSize: "clamp(60px, 12vw, 120px)" // Hero scales from 60px to 120px
    };
  };
  
  const { viewBox, className: svgClassName, fontSize } = getResponsiveSizing();

  return (
    <div className={`flex items-center justify-start ${className}`}>
      <svg
        viewBox={viewBox}
        role="img"
        aria-label={text}
        className={svgClassName}
      >
        <defs>
          {/* fill gradient (optional) */}
          <linearGradient id="potredFill" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#ff3b3b" />
            <stop offset="1" stopColor="#ff8a00" />
          </linearGradient>
        </defs>

        {/* FILL appears after stroke animation */}
        <text
          x="0"
          y="40"
          dominantBaseline="central"
          textAnchor="start"
          className="font-iansui"
          style={{
            fontSize,
            fill: L ? "url(#potredFill)" : "transparent",
            opacity: L ? 1 : 0,
            transition: "opacity .5s ease .8s",
          }}
        >
          {text}
        </text>

        {/* STROKE path-tracing with color transition */}
        <text
          ref={ref}
          x="0"
          y="40"
          dominantBaseline="central"
          textAnchor="start"
          className="font-iansui"
          style={{
            fontSize,
            fill: "none",
            stroke: "#ea580c",
            strokeWidth: stroke,
            // dash length = path length; start fully offset, animate to 0
            strokeDasharray: L ? `${L} ${L}` : undefined,
            strokeDashoffset: L ? L : undefined,
            animation: L ? `potred-draw-${animationKey} ${duration}s ease forwards, potred-stroke-color-${animationKey} ${duration}s ease forwards` : undefined,
            // nicer corners/joins for handwritten vibe
            strokeLinecap: "round",
            strokeLinejoin: "round",
          }}
        >
          {text}
        </text>
      </svg>

      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes potred-draw-${animationKey} {
            to { stroke-dashoffset: 0; }
          }
          @keyframes potred-stroke-color-${animationKey} {
            0% { stroke: #ea580c; }
            70% { stroke: #ea580c; }
            100% { stroke: #ea580c; }
          }
        `
      }} />
    </div>
  );
};

export default PotredStroke;